import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Car, TrendingUp, Trophy, Calendar, MapPin, User as UserIcon, CheckCircle2 } from 'lucide-react';
import { getUserProfile } from '../lib/events';
import VerifiedBadge from '../components/VerifiedBadge';

function money(n) { return `$${(n || 0).toLocaleString()}`; }

export default function UserProfile() {
  const { userId } = useParams();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [tab, setTab] = React.useState('active');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const p = await getUserProfile(userId);
        if (!cancelled) setData(p);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? 'notfound' : 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-8 h-8 text-[#ff2a2a] animate-spin" /></div>;
  if (error || !data) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-14 text-center">
        <UserIcon className="w-12 h-12 mx-auto text-zinc-600" />
        <h1 className="mt-4 font-display text-4xl uppercase tracking-tight">User not found</h1>
        <Link to="/marketplace" className="mt-6 inline-block bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-6 py-3 transition-colors">Back to Marketplace</Link>
      </div>
    );
  }

  const { user, verified, active_listings, sold_listings, hosted_events, stats } = data;
  const shown = tab === 'active' ? active_listings : tab === 'sold' ? sold_listings : hosted_events;

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14" data-testid="page-user-profile">
      <div className="flex items-start gap-5 flex-wrap">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full object-cover hairline" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-[#ff2a2a] text-white grid place-items-center text-4xl font-display">
            {(user.name || 'M')[0].toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-1 inline-flex items-center gap-2">
            Seller profile
            {verified && <VerifiedBadge />}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none truncate" data-testid="user-name">{user.name || 'Member'}</h1>
          {user.bio && <p className="mt-3 text-sm text-zinc-300 max-w-2xl whitespace-pre-wrap" data-testid="user-bio">{user.bio}</p>}
          {!verified && stats.sold_count > 0 && (
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              {stats.verified_threshold - stats.sold_count} more sale{stats.verified_threshold - stats.sold_count === 1 ? '' : 's'} to earn Verified Seller
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 grid sm:grid-cols-3 gap-3" data-testid="user-stats">
        <div className="am-card p-5"><Car className="w-5 h-5 text-[#ff2a2a]" /><div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Active listings</div><div className="font-display text-4xl text-white">{stats.active_count}</div></div>
        <div className="am-card p-5"><Trophy className="w-5 h-5 text-[#ff2a2a]" /><div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Vehicles sold</div><div className="font-display text-4xl text-white">{stats.sold_count}</div></div>
        <div className="am-card p-5"><Calendar className="w-5 h-5 text-[#ff2a2a]" /><div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Events hosted</div><div className="font-display text-4xl text-white">{stats.events_count}</div></div>
      </div>

      <div className="mt-10 inline-flex hairline p-1" data-testid="profile-tabs">
        {[
          ['active', `Active (${stats.active_count})`],
          ['sold',   `Sold (${stats.sold_count})`],
          ['events', `Events (${stats.events_count})`],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${tab === k ? 'bg-[#ff2a2a] text-white' : 'text-zinc-400 hover:text-white'}`}>
            {l}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="mt-6 am-card p-12 text-center text-zinc-500 text-sm" data-testid={`${tab}-empty`}>
          {tab === 'active' ? 'No active listings' : tab === 'sold' ? 'No completed sales yet' : 'No events hosted'}
        </div>
      ) : tab === 'events' ? (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((e) => (
            <div key={e.id} className="am-card overflow-hidden flex flex-col" data-testid={`user-event-${e.id}`}>
              <div className="relative aspect-[16/10] bg-[#131313]">
                {e.img && <img src={e.img} alt={e.title} className="w-full h-full object-cover" />}
                <div className="absolute top-3 left-3 am-pill bg-black/60 backdrop-blur-md">{e.category}</div>
              </div>
              <div className="p-4">
                <div className="text-white font-semibold">{e.title}</div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Calendar className="w-3 h-3"/>{e.date}{e.time ? ` · ${e.time}` : ''}</div>
                {(e.venue || e.city) && <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><MapPin className="w-3 h-3"/>{[e.venue, e.city].filter(Boolean).join(', ')}</div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((v) => (
            <Link to={`/vehicles/${v.id}`} key={v.id} className="am-card overflow-hidden flex flex-col group relative" data-testid={`user-listing-${v.id}`}>
              {v.status === 'sold' && (
                <div className="absolute top-3 left-3 z-10 bg-emerald-500 text-black px-2 py-1 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> SOLD
                </div>
              )}
              <div className="relative aspect-[4/3] overflow-hidden">
                {v.photos && v.photos[0] ? (
                  <img src={v.photos[0]} alt={v.title} className={`w-full h-full object-cover ${v.status === 'sold' ? 'grayscale opacity-70' : ''}`} />
                ) : <div className="w-full h-full bg-[#131313] grid place-items-center"><Car className="w-8 h-8 text-zinc-600"/></div>}
                <div className="absolute bottom-3 right-3 font-mono text-lg text-white bg-black/60 px-2.5 py-1 backdrop-blur-md">{money(v.price)}</div>
              </div>
              <div className="p-4">
                <div className="text-white font-semibold group-hover:text-[#ff2a2a] transition-colors">{v.title}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
